import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Button from '../../ui/Button'
import SearchInput from '../../ui/SearchInput'
import EmptyState from '../../ui/EmptyState'
import CenteredState from '../../ui/CenteredState'
import Toast from '../../shared/Toast'
import Loader from '../../shared/Loader'
import StatsCardsGroup from './StatsCardsGroup'
import StatusFilter from './StatusFilter'
import ContentFilter from './ContentFilter'
import TriggeredAlertsTable from './TriggeredAlertsTable'
import StatusInlineEditor from './StatusInlineEditor'
import AttributionInlineEditor from './AttributionInlineEditor'
import EditIssueDrawer from './EditIssueDrawer'
import ShareIssueModal from './ShareIssueModal'
import ColumnSettingsModal from './ColumnSettingsModal'
import ExportMenu from './ExportMenu'
import SelectionToolbar from './SelectionToolbar'
import useTriggeredAlerts from '../hooks/useTriggeredAlerts'
import useIssuesByStatus from '../hooks/useIssuesByStatus'
import useIssueStatuses from '../hooks/useIssueStatuses'
import useIssueMutations from '../hooks/useIssueMutations'
import useColumnPreferences from '../hooks/useColumnPreferences'
import useLazyList from '../hooks/useLazyList'
import {
  CONTENT_FILTER_OPTIONS, DEFAULT_COLUMN_PREFERENCES,
  EXTRA_COLUMN_TYPES, TOAST_CONTAINER_ID
} from './constants'
import IssuesService from '../../../services/api/IssuesService'
import CollectService from '../../../services/api/CollectService'
import { updateUrlFilters, useUrlFilters } from '../../../utils/urlFilters'

const extractQuestionSections = response => response?.sections || []
const extractVariables = response => response?.variables || []

const ISSUE_STATUS_PARAM = 'issue_status'
const CONTENT_PARAM = 'issue_content'
const VALID_CONTENT = CONTENT_FILTER_OPTIONS.map(option => option.value)
const STATUS_ID_RE = /^\d+$/u

const parseCsvParam = (param, allowed) => {
  const raw = new URLSearchParams(window.location.search).get(param)
  if (!raw)
    return []
  return raw.split(',').filter(value => allowed.includes(value))
}

// Status filter values are configured-status ids (numeric strings); the configured
// List loads async, so accept any digit token and drop stale legacy enum tokens.
const initialStatusValues = () => {
  const raw = new URLSearchParams(window.location.search).get(ISSUE_STATUS_PARAM)
  if (!raw)
    return []
  return raw.split(',').map(token => token.trim()).filter(token => STATUS_ID_RE.test(token))
}
const initialContentValues = () => parseCsvParam(CONTENT_PARAM, VALID_CONTENT)

const csvOrNull = values => values.length > 0 ? values.join(',') : null

const DEFAULT_PER_PAGE = 10
const SEARCH_DEBOUNCE_MS = 300
const EMPTY_TITLE = 'Aucune donnée'
const EMPTY_MESSAGE = 'Aucune alerte pour le moment. Configurez vos critères pour être '
  + 'notifié automatiquement lors de réponses sensibles.'
const ALERTS_HEADER_CLASS = 'tw:flex tw:flex-col tw:gap-3 tw:lg:flex-row '
  + 'tw:lg:items-center tw:lg:justify-between'
const ALERTS_HEADER_TITLE_CLASS = 'tw:m-0! tw:text-ds-neutral-highest tw:text-lg! '
  + 'tw:font-medium! tw:leading-tight'
const FULL_NAME_KEY = 'full_name'
const STATUS_ID_KEY = 'status_id'
const STATUS_NAME_KEY = 'status_name'
const STATUS_COLOR_KEY = 'status_color'
const STATUS_CATEGORY_KEY = 'status_category'

const applyStatus = (issue, issueStatus) => ({
  ...issue,
  [STATUS_ID_KEY]: issueStatus.id,
  [STATUS_NAME_KEY]: issueStatus.name,
  [STATUS_COLOR_KEY]: issueStatus.color,
  [STATUS_CATEGORY_KEY]: issueStatus.category
})

const applyAttribution = (issue, userIds, allUsers) => {
  const lookup = new Map(allUsers.map(user => [user.id, user]))
  const previous = new Map(issue.attribution.map(user => [user.id, user]))
  const attribution = userIds.map(id => lookup.get(id) || previous.get(id)).filter(Boolean)
  return { ...issue, attribution }
}

const noop = () => null

const renderFullPageState = ({
  loading, loaded, error, issues, search, statusValues, contentValues, startDate, endDate, configureUrl
}) => {
  if (loading && !loaded) {
    return (
      <div>
        <Toast containerId={TOAST_CONTAINER_ID} />
        <CenteredState><Loader /></CenteredState>
      </div>
    )
  }
  const hasActiveFilters = Boolean(search) || statusValues.length > 0
    || contentValues.length > 0 || Boolean(startDate) || Boolean(endDate)
  if (loading || error || issues.length > 0 || hasActiveFilters)
    return null

  return (
    <div>
      <Toast containerId={TOAST_CONTAINER_ID} />
      <CenteredState>
        <EmptyState
          action={configureUrl ? (
            <Button
              className="tw:text-gray-900! tw:border-gray-200!"
              href={configureUrl}
              icon="gear"
              variant="neutral"
            >
              Configurer
            </Button>
          ) : null}
          message={EMPTY_MESSAGE}
          title={EMPTY_TITLE}
        />
      </CenteredState>
    </div>
  )
}

const TriggeredAlertsTab = ({
  collectId, draftId = null, initialColumnPreferences = null, isAdmin = false
}) => {
  const [page, setPage] = useState(1)
  const [items, setItems] = useState(DEFAULT_PER_PAGE)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusValues, setStatusValues] = useState(initialStatusValues)
  const [contentValues, setContentValues] = useState(initialContentValues)
  const [sort, setSort] = useState(null)
  const [direction, setDirection] = useState('desc')
  // Optimistic overrides keyed by issueId. Cleared once the server confirms.
  const [optimisticOverrides, setOptimisticOverrides] = useState({})
  const [editorMode, setEditorMode] = useState(null)
  const [editingIssue, setEditingIssue] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [availableUsers, setAvailableUsers] = useState([])
  const [editingIssueId, setEditingIssueId] = useState(null)
  const [sharingIssueId, setSharingIssueId] = useState(null)
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false)
  // Eager: titles must resolve in the table even when the settings modal is closed.
  // Lists are owned here and forwarded to the modal as props — avoids a
  // Second round-trip when the modal opens and shows a single loader if needed.
  const {
    items: questionSections,
    loading: loadingQuestions
  } = useLazyList({
    collectId,
    isOpen: isColumnSettingsOpen,
    fetcher: CollectService.getQuestions,
    extractor: extractQuestionSections,
    eager: true
  })
  const {
    items: extraVariables,
    loading: loadingVariables
  } = useLazyList({
    collectId,
    isOpen: isColumnSettingsOpen,
    fetcher: CollectService.getVariables,
    extractor: extractVariables,
    eager: true
  })
  const optionsLoading = loadingQuestions || loadingVariables
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  // Snapshots used to rollback an optimistic update if the PATCH fails.
  const snapshotsRef = useRef({})

  const {
    preferences: columnPreferences,
    setPreferences: setColumnPreferences,
    reset: resetColumnPreferences
  } = useColumnPreferences(collectId, DEFAULT_COLUMN_PREFERENCES, initialColumnPreferences)

  const urlFilters = useUrlFilters()
  const startDate = urlFilters.start_date
  const endDate = urlFilters.end_date

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [searchInput])

  const statusParam = csvOrNull(statusValues)
  const contentParam = csvOrNull(contentValues)

  useEffect(() => {
    setPage(1)
    setSelectedIds(new Set())
  }, [debouncedSearch, statusParam, contentParam, items, sort, direction, startDate, endDate])

  const handleToggleRow = useCallback(issueId => {
    setSelectedIds(previous => {
      const next = new Set(previous)
      if (next.has(issueId))
        next.delete(issueId)
      else
        next.add(issueId)
      return next
    })
  }, [])

  const handleToggleAll = useCallback(visibleIds => {
    setSelectedIds(previous => {
      // Select-all behaviour: clear only when everything is already checked.
      // From an indeterminate state, complete the selection rather than wipe it.
      const allSelected = visibleIds.length > 0 && visibleIds.every(id => previous.has(id))
      const next = new Set(previous)
      if (allSelected)
        visibleIds.forEach(id => next.delete(id))
      else
        visibleIds.forEach(id => next.add(id))
      return next
    })
  }, [])

  const {
    issues, pagy, loading: tableLoading, hasLoaded: tableLoaded,
    error: tableError, refetch: refetchTable, mergeIssue
  } = useTriggeredAlerts(collectId, {
    page,
    items,
    search: debouncedSearch,
    issueStatus: statusParam,
    issueContent: contentParam,
    sort,
    direction,
    draftId,
    columns: columnPreferences.questionIds,
    variables: columnPreferences.variableIds,
    startDate,
    endDate
  })
  // Cards follow the same search + date range as the table so their counts stay in sync;
  // Clicking a status card filters only the table (counts stay full for navigation).
  const { groups, loading: statsLoading, refetch: refetchStats } = useIssuesByStatus(collectId, {
    search: debouncedSearch, draftId, startDate, endDate
  })
  // Configured workflow steps for the collect — feed the filter and the inline editor.
  const { statuses: issueStatuses } = useIssueStatuses(collectId)

  const handleIssueUpdated = useCallback(() => {
    refetchTable()
    refetchStats()
  }, [refetchTable, refetchStats])

  // Optimistic overrides are reconciled per row in handleMutationSuccess/Error.
  // No global reset on server changes: that would wipe a row whose edit is still
  // In flight when a concurrently edited row reconciles.

  useEffect(() => {
    if (editorMode !== 'attribution' || availableUsers.length > 0)
      return noop
    let cancelled = false
    IssuesService.getAssignableUsers(collectId).then(response => {
      if (cancelled)
        return
      const users = (response || []).map(option => ({
        id: option.user_id,
        [FULL_NAME_KEY]: option.user_name
      }))
      setAvailableUsers(users)
    })
    return () => {
      cancelled = true
    }
  }, [editorMode, availableUsers.length, collectId])

  const clearOverride = useCallback(issueId => {
    setOptimisticOverrides(previous => {
      if (!previous[issueId])
        return previous
      const next = { ...previous }
      delete next[issueId]
      return next
    })
    delete snapshotsRef.current[issueId]
  }, [])

  const handleMutationSuccess = useCallback(({ issueId, issueStatus, userIds, optimistic, issue }) => {
    if (!optimistic) {
      refetchStats()
      // No canonical payload: drop the optimistic override and refetch the truth so
      // A stale override can't linger over fresh server data.
      if (!issue) {
        clearOverride(issueId)
        refetchTable()
        return
      }
      // Surgical reconcile: replace just the mutated row with the server payload and
      // Drop its override, instead of refetching the whole table.
      mergeIssue(issue)
      clearOverride(issueId)
      return
    }
    const target = issues.find(currentIssue => currentIssue.id === issueId)
    if (!target)
      return
    if (!snapshotsRef.current[issueId])
      snapshotsRef.current[issueId] = target
    setOptimisticOverrides(previous => {
      let patched = previous[issueId] || target
      if (issueStatus)
        patched = applyStatus(patched, issueStatus)
      if (userIds)
        patched = applyAttribution(patched, userIds, availableUsers)
      return { ...previous, [issueId]: patched }
    })
  }, [issues, availableUsers, refetchStats, mergeIssue, refetchTable, clearOverride])

  const handleMutationError = useCallback(({ issueId }) => {
    clearOverride(issueId)
  }, [clearOverride])

  const displayedIssues = useMemo(() => {
    if (Object.keys(optimisticOverrides).length === 0)
      return issues
    return issues.map(issue => optimisticOverrides[issue.id] || issue)
  }, [issues, optimisticOverrides])

  const { updateStatus, updateAttribution, isUpdating } = useIssueMutations(collectId, {
    onSuccess: handleMutationSuccess,
    onError: handleMutationError,
    toastContainerId: TOAST_CONTAINER_ID
  })

  const handleSearchChange = useCallback(event => setSearchInput(event.target.value), [])
  const clearSelection = useCallback(() => setSelectedIds(new Set()), [])
  const handleStatusChange = useCallback(next => {
    setStatusValues(next)
    updateUrlFilters({ [ISSUE_STATUS_PARAM]: csvOrNull(next) })
  }, [])
  const handleContentChange = useCallback(next => {
    setContentValues(next)
    updateUrlFilters({ [CONTENT_PARAM]: csvOrNull(next) })
  }, [])
  // The table (TanStack) owns the asc → desc → none cycle and passes the next
  // Direction; storing it verbatim keeps a single source of truth.
  const handleSortChange = useCallback((column, nextDirection) => {
    setSort(column)
    setDirection(nextDirection || 'desc')
  }, [])
  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnSettingsOpen(true)
  }, [])
  const handleColumnSettingsClose = useCallback(() => {
    setIsColumnSettingsOpen(false)
  }, [])
  const handleColumnSettingsApply = useCallback(next => {
    setColumnPreferences(next)
  }, [setColumnPreferences])
  const handleColumnSettingsReset = useCallback(() => {
    resetColumnPreferences()
  }, [resetColumnPreferences])
  const handleIssueClick = useCallback(issue => {
    setEditingIssueId(issue.id)
  }, [])
  const handleShareClick = useCallback(issue => {
    setSharingIssueId(issue.id)
  }, [])
  const handleExportPdf = useCallback(issue => {
    window.open(issue.pdf_url, '_blank', 'noopener,noreferrer')
  }, [])
  const handleEditClose = useCallback(() => setEditingIssueId(null), [])
  const handleShareClose = useCallback(() => setSharingIssueId(null), [])

  const handleEditorClose = useCallback(() => {
    setEditorMode(null)
    setEditingIssue(null)
    setAnchorEl(null)
  }, [])

  // Toggle: clicking the same cell again closes the editor; switching cells reopens.
  const toggleEditor = useCallback((mode, issue, element) => {
    if (editorMode === mode && editingIssue?.id === issue.id) {
      handleEditorClose()
      return
    }
    setEditingIssue(issue)
    setAnchorEl(element)
    setEditorMode(mode)
  }, [editorMode, editingIssue, handleEditorClose])

  const openStatusEditor = (issue, element) => toggleEditor('status', issue, element)
  const handleStatusEdit = useCallback(openStatusEditor, [toggleEditor])

  const openAttributionEditor = (issue, element) => toggleEditor('attribution', issue, element)
  const handleAttributionEdit = useCallback(openAttributionEditor, [toggleEditor])

  const handleStatusSelect = useCallback(value => {
    if (!editingIssue)
      return
    const issueStatus = issueStatuses.find(status => String(status.id) === String(value))
    if (!issueStatus)
      return
    updateStatus(editingIssue.id, issueStatus.id, issueStatus)
  }, [editingIssue, issueStatuses, updateStatus])

  const handleAttributionApply = useCallback(userIds => {
    if (!editingIssue)
      return
    updateAttribution(editingIssue.id, userIds)
  }, [editingIssue, updateAttribution])

  const currentUserIds = useMemo(() => {
    if (!editingIssue)
      return []
    return editingIssue.attribution.map(user => user.id)
  }, [editingIssue])

  // Merge questions + variables into a single typed list. Each entry carries
  // Its `type` so the table can route to the right serializer key (extra_values
  // For questions, extra_variable_values for variables).
  const extraColumnsForTable = useMemo(() => {
    const allQuestions = questionSections.flatMap(section => section.questions)
    const questionLookup = new Map(allQuestions.map(question => [question.id, question]))
    const variableLookup = new Map(extraVariables.map(variable => [variable.id, variable]))
    const questions = columnPreferences.questionIds
      .map(id => questionLookup.get(id))
      .filter(Boolean)
      .map(question => ({ type: EXTRA_COLUMN_TYPES.QUESTION, id: question.id, title: question.title }))
    const variables = columnPreferences.variableIds
      .map(id => variableLookup.get(id))
      .filter(Boolean)
      .map(variable => ({ type: EXTRA_COLUMN_TYPES.VARIABLE, id: variable.id, title: variable.name }))
    return [...questions, ...variables]
  }, [
    columnPreferences.questionIds,
    columnPreferences.variableIds,
    questionSections,
    extraVariables
  ])

  const visibleColumns = useMemo(() => ({
    primary: columnPreferences.primaryColumns,
    extra: extraColumnsForTable
  }), [columnPreferences.primaryColumns, extraColumnsForTable])

  const configureUrl = isAdmin && !draftId ? `/collects/${collectId}/alerts` : null
  const fullPageState = renderFullPageState({
    loading: tableLoading,
    loaded: tableLoaded,
    error: tableError,
    issues,
    search: debouncedSearch,
    statusValues,
    contentValues,
    startDate,
    endDate,
    configureUrl
  })
  if (fullPageState)
    return fullPageState

  const filters = (
    <div className="tw:flex tw:items-center tw:gap-3">
      {selectedIds.size === 0 ? (
        <div className="tw:w-52">
          <ContentFilter value={contentValues} onChange={handleContentChange} />
        </div>
      ) : null}
      <div className="tw:w-52">
        <StatusFilter statuses={issueStatuses} value={statusValues} onChange={handleStatusChange} />
      </div>
    </div>
  )

  return (
    <div>
      <Toast containerId={TOAST_CONTAINER_ID} />
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
          <div className="tw:w-full tw:max-w-[531px]">
            <SearchInput
              name="alerts-search"
              placeholder="Rechercher une alerte, attribution, ressource,..."
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
          <div className="tw:flex tw:items-center tw:gap-3">
            {configureUrl ? (
              <Button
                className="tw:text-gray-700! tw:hover:bg-gray-50!"
                href={configureUrl}
                icon="gear"
                variant="neutral"
              >
                Configuration
              </Button>
            ) : null}
            <ExportMenu
              collectId={collectId}
              draftId={draftId}
              issueContent={contentParam}
              issueStatus={statusParam}
              search={debouncedSearch}
              selectedIds={selectedIds}
              toastContainerId={TOAST_CONTAINER_ID}
            />
          </div>
        </div>
        <StatsCardsGroup groups={groups} loading={statsLoading} />
        <div className={ALERTS_HEADER_CLASS}>
          {selectedIds.size > 0 ? (
            <SelectionToolbar
              collectId={collectId}
              draftId={draftId}
              issueContent={contentParam}
              issueStatus={statusParam}
              search={debouncedSearch}
              selectedIds={selectedIds}
              toastContainerId={TOAST_CONTAINER_ID}
              onClear={clearSelection}
            />
          ) : <h3 className={ALERTS_HEADER_TITLE_CLASS}>Alertes déclenchées</h3>}
          {filters}
        </div>
        <TriggeredAlertsTable
          activeIssueId={editingIssue?.id || editingIssueId || sharingIssueId || null}
          direction={direction}
          error={tableError}
          isIssueUpdating={isUpdating}
          issues={displayedIssues}
          loading={tableLoading}
          pagy={pagy}
          selectedIds={selectedIds}
          sort={sort}
          visibleColumns={visibleColumns}
          onAttributionEdit={handleAttributionEdit}
          onColumnSettingsClick={handleColumnSettingsClick}
          onExportPdf={handleExportPdf}
          onIssueClick={handleIssueClick}
          onPageChange={setPage}
          onPerPageChange={setItems}
          onShareClick={handleShareClick}
          onSortChange={handleSortChange}
          onStatusEdit={handleStatusEdit}
          onToggleAll={handleToggleAll}
          onToggleRow={handleToggleRow}
        />
      </div>
      <StatusInlineEditor
        anchorEl={anchorEl}
        currentStatusId={editingIssue?.status_id ?? null}
        isOpen={editorMode === 'status'}
        issueStatuses={issueStatuses}
        onClose={handleEditorClose}
        onSelect={handleStatusSelect}
      />
      <AttributionInlineEditor
        anchorEl={anchorEl}
        availableUsers={availableUsers}
        currentUserIds={currentUserIds}
        isOpen={editorMode === 'attribution'}
        onApply={handleAttributionApply}
        onClose={handleEditorClose}
      />
      <EditIssueDrawer
        collectId={collectId}
        isOpen={editingIssueId !== null}
        issueId={editingIssueId}
        issueStatuses={issueStatuses}
        onClose={handleEditClose}
        onUpdated={handleIssueUpdated}
      />
      <ShareIssueModal
        collectId={collectId}
        isOpen={sharingIssueId !== null}
        issueId={sharingIssueId}
        onClose={handleShareClose}
      />
      <ColumnSettingsModal
        isOpen={isColumnSettingsOpen}
        optionsLoading={optionsLoading}
        preferences={columnPreferences}
        questionSections={questionSections}
        variables={extraVariables}
        onApply={handleColumnSettingsApply}
        onClose={handleColumnSettingsClose}
        onReset={handleColumnSettingsReset}
      />
    </div>
  )
}

TriggeredAlertsTab.propTypes = {
  collectId: PropTypes.number.isRequired,
  draftId: PropTypes.number,
  initialColumnPreferences: PropTypes.shape({}),
  isAdmin: PropTypes.bool
}

TriggeredAlertsTab.defaultProps = {
  draftId: null,
  initialColumnPreferences: null,
  isAdmin: false
}

export default TriggeredAlertsTab
